import { useState } from "react";
import { comments } from "@/mocks/home";

const avatarTones = [
  "bg-primary-100 text-primary-700",
  "bg-accent-100 text-accent-800",
  "bg-secondary-200 text-secondary-900",
  "bg-primary-200 text-primary-900",
];

export default function Community() {
  const [likes, setLikes] = useState<Record<string, number>>(() =>
    comments.reduce<Record<string, number>>((acc, c) => {
      acc[c.id] = c.likes;
      return acc;
    }, {})
  );
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [sort, setSort] = useState("热门");

  const sorted = sort === "热门" ? [...comments] : [...comments].reverse();

  const toggleLike = (id: string) => {
    setLiked((prev) => {
      const isNow = !prev[id];
      setLikes((l) => ({ ...l, [id]: l[id] + (isNow ? 1 : -1) }));
      return { ...prev, [id]: isNow };
    });
  };

  return (
    <section id="talk" className="border-t border-background-200 bg-background-100 py-10 md:py-14">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-7 px-4 md:px-6">
        {/* 分区头 */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-900">
                <i className="ri-chat-3-fill text-lg" />
              </span>
              <h3 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">大家说</h3>
            </div>
            <p className="mt-2 text-sm text-foreground-500">
              讨论区才是吃瓜的主战场。补楼、互相指正、认领实锤，都能在这里发生。
            </p>
          </div>

          <div className="flex w-fit items-center gap-1 rounded-full bg-secondary-100 p-1">
            {["热门", "最新"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  sort === s ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-7 lg:grid-cols-12">
          {/* 评论流 */}
          <div className="flex flex-col gap-4 lg:col-span-8">
            {sorted.map((c, idx) => (
              <article key={c.id} className="rounded-lg bg-background-50 p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold ${avatarTones[idx % avatarTones.length]}`}
                  >
                    {c.name.slice(0, 1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground-950">{c.name}</span>
                      {c.badge && (
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs text-accent-900">{c.badge}</span>
                      )}
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{c.tag}</span>
                      <span className="text-xs text-foreground-400">{c.time}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{c.content}</p>

                    <div className="mt-3 flex items-center gap-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                      <button
                        type="button"
                        onClick={() => toggleLike(c.id)}
                        className={`flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-secondary-100 ${
                          liked[c.id] ? "text-primary-600" : ""
                        }`}
                      >
                        <i className={`${liked[c.id] ? "ri-heart-fill" : "ri-heart-line"} text-sm`} />
                        {likes[c.id]}
                      </button>
                      <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-secondary-100"
                      >
                        <i className="ri-chat-4-line text-sm" />
                        {c.replies} 回复
                      </button>
                      <button
                        type="button"
                        className="ml-auto flex items-center gap-1.5 rounded-full px-2 py-1 transition-colors hover:bg-secondary-100"
                      >
                        <i className="ri-share-forward-line text-sm" />
                        转发
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))}

            <div className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-foreground-200 py-5 text-sm text-foreground-500">
              <i className="ri-arrow-down-line" />
              已到底，可到评论区补楼或发起新话题
            </div>
          </div>

          {/* 评论工具区 */}
          <div className="flex flex-col gap-5 lg:col-span-4">
            <div className="sticky top-20 flex flex-col gap-4 rounded-lg bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-edit-2-line text-primary-500" />
                <p className="text-base font-semibold text-foreground-950">补一句 / 补一条线索</p>
              </div>
              <textarea
                rows={4}
                maxLength={500}
                placeholder="写下你的看法，或补充一条新证据、原帖链接……"
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 p-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              <div className="flex flex-col gap-2.5 sm:flex-row">
                <button
                  type="button"
                  className="whitespace-nowrap flex-1 rounded-full bg-primary-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  发表评论
                </button>
                <button
                  type="button"
                  className="whitespace-nowrap flex-1 rounded-full border border-foreground-300 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-foreground-950"
                >
                  投稿爆料
                </button>
              </div>
              <p className="text-xs leading-relaxed text-foreground-500">
                讨论需理性。涉及事实的部分欢迎互相引用一手来源指正，避免以谣传谣。
              </p>
            </div>

            <div className="flex flex-col gap-3 rounded-lg bg-background-50 p-5">
              <p className="text-sm font-semibold text-foreground-950">补充线索小贴士</p>
              <ul className="flex flex-col gap-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                  尽量附原始链接或截图，方便别人二次核对
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                  标记「待实锤」的线索不要当结论传播
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                  恶意揣测人身的内容会被折叠并移出
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}